import { Link } from "react-router-dom";
import Nav from "../../components/Nav";
import Footer from "../../components/Footer";
import Reveal from "../../components/Reveal";

const THESIS_URL = `${process.env.PUBLIC_URL}/cv/msc-thesis-james-carl-rhule.docx`;

const STATS = [
  { value: "87", label: "Respondents (94.6% Response)" },
  { value: "75.2%", label: "Variance Explained (R²)" },
  { value: "β .439", label: "Digital Technology" },
  { value: "0.873", label: "Instrument Reliability" },
];

const BETAS = [
  ["Digital Technology", "β = .439", 100],
  ["Leadership Style", "β = .302", 69],
  ["Organisational Structure", "β = .253", 58],
];

const FINDINGS = [
  ["Digital technology leads", "Digital technology was the strongest predictor of sustainable development (β = .439, p < .001), and also had the highest mean score of the three predictors (4.24)."],
  ["Leadership comes second", "Leadership style was a significant positive predictor (β = .302, p < .001). Respondents linked leaders who involve staff in decisions with a more productive, sustainable institution."],
  ["Structure is the foundation", "Organisational structure was significant (β = .253, p = .001) but the weakest of the three, a necessary base whose effect is slower to change than leadership or technology."],
  ["Together they explain three-quarters", "The model was significant (F = 83.99, p < .001) and explained 75.2% of the variation in sustainable development. All three hypotheses were supported."],
  ["Factors are connected", "The predictors correlated with each other (r = .59 to .67), supporting a systems view: technology works best where structure and leadership let it."],
];

const RECOMMENDATIONS = [
  ["Streamline structures", "Review structures regularly, remove unnecessary bureaucracy and extra approval layers, and clarify responsibilities."],
  ["Develop leaders", "Run continuous leadership development in transformational leadership, ethical governance and change management, with practical participatory methods."],
  ["Invest in digital infrastructure", "Expand e-government systems, connectivity and cybersecurity, including back-end data analytics and decision-support."],
  ["Build digital skills", "Train staff in digital service delivery and records management, prioritising departments with less exposure."],
  ["Foster collaboration", "Promote integrated planning and shared digital collaboration platforms across departments."],
];

const PROCESS = [
  ["Design", "A quantitative, cross-sectional descriptive survey design testing three hypotheses on the drivers of sustainable development."],
  ["Sample", "87 staff from the Administration, Finance, Human Resource and Information Technology departments of public institutions, from 92 questionnaires distributed (94.6% returned)."],
  ["Instrument", "A structured questionnaire, piloted with ten respondents. Cronbach's alpha ranged from 0.842 to 0.889 across constructs, 0.873 overall."],
  ["Analysis", "Descriptive statistics, Pearson correlation and multiple regression in SPSS to test hypotheses and the combined model."],
  ["Limitations", "Findings show association, not causation, and rely on self-reported perceptions from a limited set of institutions. Future work could use mixed methods and longitudinal designs."],
];

const INFO = [
  ["Degree", "MSc Leadership and Organisational Development"],
  ["Institution", "University for Development Studies, Tamale"],
  ["Department", "Human Resource and Organisational Development"],
  ["Supervisor", "Dr. Stephen Tetteh"],
  ["Year", "2026"],
  ["Status", "Programme complete; award pending"],
];

export default function PublicInstitutionsStudy() {
  return (
    <>
      <Nav />

      <div className="mx-auto max-w-content px-6 pt-32 md:px-12 md:pt-40">
        <Link to="/initiatives" className="text-[0.72rem] uppercase tracking-widest2 text-ink-faint hover:text-teal-900">
          &larr; Back to Experience &amp; Programmes
        </Link>
      </div>

      <Reveal className="mx-auto max-w-content px-6 pb-14 pt-8 md:px-12 md:pb-20">
        <p className="eyebrow">MSc Research &middot; Public Sector Governance</p>
        <h1 className="mt-3 max-w-[26ch] font-serif text-4xl font-medium leading-tight text-teal-900 md:text-5xl">
          Organisational Structure, Leadership Style and Digital Technology in Promoting Sustainable Development
        </h1>
        <p className="mt-6 max-w-[62ch] text-[1rem] leading-relaxed text-ink-soft">
          A study of public institutions in Ghana examining how the way organisations are structured,
          the style of their leadership and their use of digital technology each contribute to
          sustainable development, and how much they explain together.
        </p>
        <a
          href={THESIS_URL}
          className="mt-8 inline-block border border-teal-900 bg-teal-900 px-6 py-3 text-[0.72rem] uppercase tracking-widest2 text-white transition-colors hover:bg-teal-700"
        >
          Download the Thesis &rarr;
        </a>
      </Reveal>

      <Reveal as="section" className="border-y border-line bg-stone-50">
        <div className="mx-auto grid max-w-content grid-cols-2 divide-x divide-line px-6 sm:grid-cols-4 md:px-12">
          {STATS.map((s) => (
            <div key={s.label} className="px-2 py-8 text-center first:pl-0 sm:px-6">
              <div className="font-serif text-3xl text-teal-900">{s.value}</div>
              <div className="mt-1 text-[0.65rem] uppercase tracking-widest2 text-ink-faint">{s.label}</div>
            </div>
          ))}
        </div>
      </Reveal>

      <div className="mx-auto max-w-content px-6 py-16 md:px-12 md:py-24">
        <div className="grid gap-14 md:grid-cols-[1fr_320px] md:gap-16">
          <div>
            <Reveal className="mb-16">
              <p className="eyebrow">Overview</p>
              <h2 className="mt-2 font-serif text-2xl text-teal-900">The question</h2>
              <p className="mt-4 max-w-[62ch] text-[0.95rem] leading-relaxed text-ink-soft">
                Governments rely on public institutions to deliver sustainable development, yet it is
                unclear which institutional levers matter most. This study asked whether organisational
                structure, leadership style and digital technology each influence sustainable development
                in Ghana&rsquo;s public sector, which matters most, and how well they explain it together.
              </p>
            </Reveal>

            <Reveal className="mb-16">
              <p className="eyebrow">Relative Influence</p>
              <h2 className="mt-2 font-serif text-2xl text-teal-900">Standardised regression coefficients</h2>
              <div className="mt-6 space-y-5">
                {BETAS.map(([label, beta, pct]) => (
                  <div key={label}>
                    <div className="flex items-baseline justify-between text-[0.85rem]">
                      <span className="font-serif text-base text-teal-900">{label}</span>
                      <span className="text-ink-soft">{beta}</span>
                    </div>
                    <div className="mt-2 h-2 w-full bg-stone-100">
                      <div className="h-2 bg-teal-700" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal className="mb-16">
              <p className="eyebrow">Key Findings</p>
              <h2 className="mt-2 font-serif text-2xl text-teal-900">What the data showed</h2>
              <div className="mt-6 divide-y divide-line border-t border-line">
                {FINDINGS.map(([title, desc]) => (
                  <div key={title} className="grid gap-1 py-5 md:grid-cols-[220px_1fr] md:gap-8">
                    <p className="font-serif text-base text-teal-900">{title}</p>
                    <p className="text-[0.9rem] leading-relaxed text-ink-soft">{desc}</p>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal className="mb-16">
              <p className="eyebrow">Recommendations</p>
              <h2 className="mt-2 font-serif text-2xl text-teal-900">What the study proposes</h2>
              <div className="mt-6 divide-y divide-line border-t border-line">
                {RECOMMENDATIONS.map(([title, desc]) => (
                  <div key={title} className="grid gap-1 py-5 md:grid-cols-[220px_1fr] md:gap-8">
                    <p className="font-serif text-base text-teal-900">{title}</p>
                    <p className="text-[0.9rem] leading-relaxed text-ink-soft">{desc}</p>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal>
              <p className="eyebrow">Method</p>
              <h2 className="mt-2 font-serif text-2xl text-teal-900">How the study was carried out</h2>
              <div className="mt-6 space-y-6">
                {PROCESS.map(([title, desc], i) => (
                  <div key={title} className="flex gap-5 border-t border-line pt-5">
                    <span className="font-serif text-lg text-ink-faint">{String(i + 1).padStart(2, "0")}</span>
                    <div>
                      <p className="font-serif text-base text-teal-900">{title}</p>
                      <p className="mt-1 text-[0.9rem] leading-relaxed text-ink-soft">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          <Reveal as="aside" className="h-fit border border-line-strong p-7 md:sticky md:top-28">
            <p className="eyebrow mb-5">Project Info</p>
            <dl className="space-y-4 text-[0.85rem]">
              {INFO.map(([label, value]) => (
                <div key={label} className="border-t border-line pt-3 first:border-t-0 first:pt-0">
                  <dt className="text-[0.65rem] uppercase tracking-widest2 text-ink-faint">{label}</dt>
                  <dd className="mt-1 text-teal-900">{value}</dd>
                </div>
              ))}
            </dl>
          </Reveal>
        </div>
      </div>

      <Reveal as="section" className="bg-teal-900">
        <div className="mx-auto max-w-content px-6 py-16 md:px-12 md:py-24">
          <p className="text-[0.68rem] uppercase tracking-widest2 text-teal-300">Why it matters to my work</p>
          <h2 className="mt-2 font-serif text-2xl font-medium text-white md:text-3xl">
            What this research means in practice
          </h2>
          <div className="mt-10 grid gap-px border-y border-white/15 sm:grid-cols-3">
            <div className="border-t border-white/15 py-6 pr-6 sm:border-t-0">
              <div className="font-serif text-2xl text-white">Evidence First</div>
              <p className="mt-2 text-[0.85rem] leading-relaxed text-teal-100/75">
                Decisions about institutional reform are stronger when they rest on measured
                evidence, the same principle behind my monitoring and evaluation work.
              </p>
            </div>
            <div className="border-t border-white/15 py-6 sm:border-t-0 sm:px-6">
              <div className="font-serif text-2xl text-white">Lead by Involving</div>
              <p className="mt-2 text-[0.85rem] leading-relaxed text-teal-100/75">
                Participatory leadership builds the trust and ownership that lasting change
                depends on, and guides how I supervise and develop my teams.
              </p>
            </div>
            <div className="border-t border-white/15 py-6 pl-0 sm:border-t-0 sm:pl-6">
              <div className="font-serif text-2xl text-white">Digital as a Multiplier</div>
              <p className="mt-2 text-[0.85rem] leading-relaxed text-teal-100/75">
                Technology delivers most when structure and leadership are ready for it, a
                priority for modernising service delivery in public institutions.
              </p>
            </div>
          </div>
        </div>
      </Reveal>

      <section className="border-t border-line">
        <div className="mx-auto flex max-w-content flex-wrap items-center justify-between gap-6 px-6 py-14 md:px-12">
          <p className="font-serif text-xl text-teal-900">See all experience, programmes &amp; research</p>
          <Link
            to="/initiatives"
            className="border border-teal-900 bg-teal-900 px-6 py-3 text-[0.72rem] uppercase tracking-widest2 text-white transition-colors hover:bg-teal-700"
          >
            Browse All &rarr;
          </Link>
        </div>
      </section>

      <Footer />
    </>
  );
}
